import { Button, Drawer, Empty, Flex, Skeleton, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import { getProjectDocumentActivity } from "../../Project/api/project-documents.services.ts"
import { type DocumentActivity } from "../../Project/types/project-attachment-workflow.types.ts"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

const actorName = (activity: DocumentActivity) => getEntityName(activity.actor)

function dayLabel(date: string, t: (key: string) => string): string {
  const day = dayjs(date)
  if (day.isSame(dayjs(), "day")) return t("files.activity.today")
  if (day.isSame(dayjs().subtract(1, "day"), "day")) return t("files.activity.yesterday")
  return getFormatDate(date) ?? day.format("MMMM D, YYYY")
}

interface ActivityDrawerProps {
  file: FileItem
  onClose: () => void
}

export function ActivityDrawer({ file, onClose }: ActivityDrawerProps) {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<DocumentActivity[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setLoadFailed(false)
    void getProjectDocumentActivity(file.id).then((response) => {
      if (cancelled) return
      if (response.ok && response.data) {
        setEntries(response.data)
      } else {
        setEntries([])
        setLoadFailed(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [file.id, reloadCount])

  const groups: { label: string; items: DocumentActivity[] }[] = []
  for (const entry of entries ?? []) {
    const label = dayLabel(entry.created, t)
    const group = groups.find((g) => g.label === label)
    if (group) group.items.push(entry)
    else groups.push({ label, items: [entry] })
  }

  return (
    // Default (portaled) container — see ReviewDrawer's note: this can open
    // from within the FileViewer modal, which an inline drawer would render behind.
    <Drawer
      title={t("files.activity.panelTitle", { name: file.name })}
      open
      onClose={onClose}
      width={380}
      maskClosable
    >
      {entries === null ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : loadFailed ? (
        <Empty description={t("files.activity.loadFailed")}>
          <Button size="small" onClick={() => setReloadCount((count) => count + 1)}>
            {t("files.activity.retry")}
          </Button>
        </Empty>
      ) : entries.length === 0 ? (
        <Empty description={t("files.activity.empty")} />
      ) : (
        <Flex vertical gap="large">
          {groups.map((group) => (
            <Flex key={group.label} vertical gap={8}>
              <Text type="secondary" className="text-xs uppercase tracking-wide">
                {group.label}
              </Text>
              {group.items.map((entry) => (
                <Flex key={entry.id} justify="space-between" align="baseline" gap={8}>
                  <Text className="text-sm">
                    {t(`files.activity.entries.${entry.activity_type}`, {
                      actor: actorName(entry),
                      defaultValue: `${actorName(entry)} · ${entry.activity_type_label}`
                    })}
                  </Text>
                  <Text type="secondary" className="text-xs whitespace-nowrap">
                    {dayjs(entry.created).format("h:mm A")}
                  </Text>
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>
      )}
    </Drawer>
  )
}
